import type { Part } from "@mux/sdk/v2/client"
import type { PartDeltaEvent } from "../event-validation"
import { appendPositionedText } from "../positioned-text"

type Chunk = { delta: string; byteOffset: number; bytes: number }
type Gap = { messageID: string; partID: string; base: string; end: number; chunks: Chunk[] }
type Recovery = {
  directory: string
  sessionID: string
  gaps: Map<string, Gap>
  bytes: number
  chunks: number
  running: boolean
  failures: number
  timer?: ReturnType<typeof setTimeout>
}

// Match the event stream's 1 MiB / 256-event recovery budget. Overflow requests
// a fresh authoritative snapshot instead of retaining an unbounded suffix.
const maxBytes = 1 << 20
const maxChunks = 256
const encoder = new TextEncoder()

export function createTextGapRecovery(input: {
  load: (directory: string, sessionID: string) => Promise<void>
  getParts: (messageID: string) => Part[] | undefined
  setParts: (messageID: string, parts: Part[]) => void
  report: (error: unknown) => void
}) {
  const sessions = new Map<string, Recovery>()
  let disposed = false
  const key = (directory: string, sessionID: string) => `${directory}\n${sessionID}`
  const active = (state: Recovery) => !disposed && sessions.get(key(state.directory, state.sessionID)) === state
  const forget = (state: Recovery) => {
    clearTimeout(state.timer)
    sessions.delete(key(state.directory, state.sessionID))
  }
  const remove = (state: Recovery, id: string, gap: Gap) => {
    for (const chunk of gap.chunks) state.bytes -= chunk.bytes
    state.chunks -= gap.chunks.length
    state.gaps.delete(id)
  }
  const flush = (state: Recovery, messageID?: string) => {
    for (const [id, gap] of state.gaps) {
      if (messageID && gap.messageID !== messageID) continue
      const parts = input.getParts(gap.messageID)
      const index = parts?.findIndex((part) => part.id === gap.partID) ?? -1
      const part = parts?.[index]
      if (!part) continue
      if ((part.type !== "text" && part.type !== "reasoning") || !part.text.startsWith(gap.base)) {
        remove(state, id, gap)
        continue
      }
      let text = part.text
      while (gap.chunks.length) {
        const chunk = gap.chunks[0]!
        const next = appendPositionedText(part, text, chunk.delta, chunk.byteOffset)
        if (next === undefined) break
        text = next
        gap.chunks.shift()
        state.bytes -= chunk.bytes
        state.chunks--
      }
      if (text !== part.text) input.setParts(gap.messageID, parts!.map((item, at) => at === index ? { ...part, text } : item))
      if (!gap.chunks.length && appendPositionedText(part, text, "", gap.end) !== undefined) state.gaps.delete(id)
    }
    if (!state.gaps.size && !state.running) forget(state)
  }
  const run = async (state: Recovery) => {
    if (!active(state) || state.running) return
    clearTimeout(state.timer)
    state.timer = undefined
    state.running = true
    try {
      await input.load(state.directory, state.sessionID)
      if (!active(state)) return
      flush(state)
    } catch (error) {
      if (!active(state)) return
      if (state.failures === 0) input.report(error)
    } finally {
      state.running = false
      if (active(state)) {
        if (!state.gaps.size) forget(state)
        else {
          // Checkpoints are written at one-second intervals. A lagging snapshot
          // gets bounded backoff, not another request for every arriving token.
          const delay = Math.min(1_000 * 2 ** Math.min(state.failures++, 4), 15_000)
          state.timer = setTimeout(() => void run(state), delay)
        }
      }
    }
  }
  return {
    receive(directory: string, sessionID: string, delta: PartDeltaEvent["properties"]) {
      if (disposed || delta.field !== "text" || delta.byteOffset === undefined || !delta.delta) return
      const id = key(directory, sessionID)
      let state = sessions.get(id)
      if (!state) {
        state = { directory, sessionID, gaps: new Map(), bytes: 0, chunks: 0, running: false, failures: 0 }
        sessions.set(id, state)
      }
      const partKey = `${delta.messageID}\n${delta.partID}`
      let gap = state.gaps.get(partKey)
      const bytes = encoder.encode(delta.delta).length
      const previous = gap?.chunks.at(-1)
      if (previous && previous.byteOffset === delta.byteOffset && previous.delta === delta.delta) return
      if (!gap) {
        const part = input.getParts(delta.messageID)?.find((part) => part.id === delta.partID)
        gap = { messageID: delta.messageID, partID: delta.partID, base: part?.type === "text" || part?.type === "reasoning" ? part.text : "", end: 0, chunks: [] }
        state.gaps.set(partKey, gap)
      }
      gap.end = Math.max(gap.end, delta.byteOffset + bytes)
      if (state.bytes + bytes > maxBytes || state.chunks >= maxChunks) {
        // Retain coverage targets even when dropping buffered bytes. A checkpoint
        // older than the discarded suffix must not falsely finish recovery.
        for (const pending of state.gaps.values()) pending.chunks = []
        state.bytes = state.chunks = 0
        input.report(new Error("Transcript recovery buffer exceeded its event budget; requesting a fresh snapshot"))
      } else {
        gap.chunks.push({ delta: delta.delta, byteOffset: delta.byteOffset, bytes })
        gap.chunks.sort((a, b) => a.byteOffset - b.byteOffset)
        state.bytes += bytes
        state.chunks++
      }
      if (!state.running && state.timer === undefined) void run(state)
    },
    updated(directory: string, sessionID: string, messageID: string) {
      if (!sessions.size) return
      const state = sessions.get(key(directory, sessionID))
      if (state) flush(state, messageID)
    },
    removed(directory: string, sessionID: string, messageID: string, partID?: string) {
      const state = sessions.get(key(directory, sessionID))
      if (!state) return
      for (const [id, gap] of state.gaps) {
        if (gap.messageID === messageID && (!partID || gap.partID === partID)) remove(state, id, gap)
      }
      if (!state.gaps.size) forget(state)
    },
    clear(directory: string, sessionID: string) {
      const state = sessions.get(key(directory, sessionID))
      if (state) forget(state)
    },
    dispose() {
      disposed = true
      for (const state of sessions.values()) clearTimeout(state.timer)
      sessions.clear()
    },
  }
}
